import {
  GM_MONSTER_PLACEHOLDER_IMAGE,
  type GmMonsterTemplate,
} from "@/gm/gm-monster-catalog";

export type DungeonFloorMonsterPickerDeps = {
  catalog: GmMonsterTemplate[];
  listMonsterIds: () => string[];
  vaultCount: (monsterId: string) => number;
  maxForMonster: (monsterId: string) => number;
  onPick: (monsterId: string) => void;
};

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function monsterThumb(m: GmMonsterTemplate | undefined): string {
  if (!m) return GM_MONSTER_PLACEHOLDER_IMAGE;
  return m.imageDataUrl?.startsWith("data:") ? m.imageDataUrl : GM_MONSTER_PLACEHOLDER_IMAGE;
}

export function mountDungeonFloorMonsterPickerModal(
  container: HTMLElement,
  deps: DungeonFloorMonsterPickerDeps,
): {
  open: () => void;
  close: () => void;
  destroy: () => void;
} {
  const modal = document.createElement("div");
  modal.id = "dungeon-monster-picker-modal";
  modal.className = "gm-monster-modal dungeon-monster-picker-modal";
  modal.hidden = true;
  modal.setAttribute("aria-hidden", "true");
  modal.innerHTML = `
    <button type="button" class="gm-monster-modal-backdrop" data-dungeon-picker-close aria-label="ปิด"></button>
    <div class="gm-monster-modal-dialog dungeon-monster-picker-dialog" role="dialog" aria-modal="true" aria-labelledby="dungeon-monster-picker-title">
      <header class="gm-monster-modal-head">
        <h2 class="gm-monster-modal-title" id="dungeon-monster-picker-title">เลือกมอนสเตอร์</h2>
        <button type="button" class="btn btn-ghost btn-sm gm-monster-modal-close" data-dungeon-picker-close aria-label="ปิด">✕</button>
      </header>
      <div class="gm-monster-modal-body dungeon-monster-picker-body" id="dungeon-monster-picker-body"></div>
    </div>`;
  container.appendChild(modal);

  const renderList = () => {
    const body = modal.querySelector<HTMLElement>("#dungeon-monster-picker-body");
    if (!body) return;
    const ids = deps.listMonsterIds();
    if (ids.length === 0) {
      body.innerHTML = `
        <p class="hint home-shop-empty">ไม่มีมอนสเตอร์ให้เลือก — ซื้อที่ร้านหรือลบประเภทที่ซ้ำในด่าน</p>
        <p class="hint"><a href="/home?shop=monsters">ไปร้านมอนสเตอร์</a></p>`;
      return;
    }
    body.innerHTML = `
      <ul class="dungeon-monster-picker-list" role="list">
        ${ids
          .map((id) => {
            const m = deps.catalog.find((c) => c.id === id);
            const name = m?.name ?? id;
            const vault = deps.vaultCount(id);
            const cap = deps.maxForMonster(id);
            const disabled = cap <= 0;
            return `
          <li role="listitem">
            <button type="button" class="dungeon-monster-picker-item" data-pick-monster-id="${escapeHtml(id)}" ${disabled ? "disabled" : ""}>
              <img class="dungeon-monster-picker-thumb" src="${escapeHtml(monsterThumb(m))}" alt="" width="48" height="48" />
              <span class="dungeon-monster-picker-meta">
                <span class="dungeon-monster-picker-name">${escapeHtml(name)}</span>
                <span class="hint dungeon-monster-picker-stock">คลัง ${vault.toLocaleString("en-US")} · ใส่ได้ ${cap.toLocaleString("en-US")}</span>
              </span>
              <span class="dungeon-monster-picker-chevron" aria-hidden="true">›</span>
            </button>
          </li>`;
          })
          .join("")}
      </ul>`;

    body.querySelectorAll<HTMLButtonElement>("[data-pick-monster-id]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const monsterId = btn.dataset.pickMonsterId;
        if (!monsterId || btn.disabled) return;
        deps.onPick(monsterId);
        close();
      });
    });
  };

  const open = () => {
    renderList();
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    document.body.classList.add("gm-monster-modal-open");
  };

  const close = () => {
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    const spawnModal = container.querySelector<HTMLElement>("#dungeon-spawn-modal");
    if (!spawnModal || spawnModal.hidden) {
      document.body.classList.remove("gm-monster-modal-open");
    }
  };

  modal.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    if (target.closest("[data-dungeon-picker-close]")) {
      close();
    }
  });

  return {
    open,
    close,
    destroy: () => {
      close();
      modal.remove();
    },
  };
}
